'use client';

import { useCallback, useEffect, useRef, useState } from "react";
import { PhotoIcon, XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/solid";

// Photos are uploaded one-by-one to our own API (which relays them to a
// Google Drive shared folder) rather than attached to the outgoing email —
// Vercel serverless functions hard-cap a single request around 4.5MB, and
// attaching raw files to an email doesn't scale past a couple of photos.
// Uploading individually, already compressed client-side, sidesteps both
// limits and lets us support a much higher photo count.
const MAX_FILES = 30;
const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.75;

async function compressImage(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
  );

  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

export default function PhotoUpload({ name = "form_photos_link", onUploadingChange }) {
  const [items, setItems] = useState([]); // [{ key, previewUrl, status, error }]
  const [isDragging, setIsDragging] = useState(false);
  const [photosLink, setPhotosLink] = useState("");
  const folderRef = useRef({ id: null, link: null });

  useEffect(() => {
    return () => {
      items.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const uploading = items.some((it) => it.status === "compressing" || it.status === "uploading");
    onUploadingChange?.(uploading);
  }, [items, onUploadingChange]);

  const uploadOne = useCallback(async (key, compressedFile) => {
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, status: "uploading" } : it))
    );

    try {
      const body = new FormData();
      body.append("photo", compressedFile);
      if (folderRef.current.id) body.append("folderId", folderRef.current.id);

      const response = await fetch("/api/photos/upload", { method: "POST", body });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || "Échec de l'envoi.");
      }

      if (!folderRef.current.id) {
        folderRef.current = { id: data.folderId, link: data.folderLink };
        setPhotosLink(data.folderLink || "");
      }

      setItems((prev) =>
        prev.map((it) => (it.key === key ? { ...it, status: "done" } : it))
      );
    } catch (err) {
      setItems((prev) =>
        prev.map((it) =>
          it.key === key ? { ...it, status: "error", error: err.message || "Erreur d'envoi." } : it
        )
      );
    }
  }, []);

  const addFiles = useCallback(
    async (fileList) => {
      const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
      if (incoming.length === 0) return;

      const room = MAX_FILES - items.length;
      const accepted = incoming.slice(0, Math.max(room, 0));
      const overflow = incoming.length - accepted.length;

      const pending = accepted.map((file) => ({
        key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        original: file,
        previewUrl: URL.createObjectURL(file),
        status: "compressing",
        error: overflow > 0 ? null : null,
      }));

      setItems((prev) => [...prev, ...pending]);

      if (overflow > 0) {
        // Surface the truncation as a one-off notice rather than silently
        // dropping files the visitor thought they'd attached.
        window.setTimeout(() => {
          alert(
            `Seules ${accepted.length} photo(s) ont été ajoutées (maximum ${MAX_FILES}). ${overflow} photo(s) ignorée(s).`
          );
        }, 0);
      }

      for (const pendingItem of pending) {
        try {
          const compressed = await compressImage(pendingItem.original);
          await uploadOne(pendingItem.key, compressed);
        } catch {
          setItems((prev) =>
            prev.map((it) =>
              it.key === pendingItem.key
                ? { ...it, status: "error", error: "Impossible de traiter cette photo." }
                : it
            )
          );
        }
      }
    },
    [items.length, uploadOne]
  );

  const removeItem = (target) => {
    setItems((prev) => {
      URL.revokeObjectURL(target.previewUrl);
      return prev.filter((it) => it.key !== target.key);
    });
  };

  const retryItem = async (target) => {
    setItems((prev) =>
      prev.map((it) => (it.key === target.key ? { ...it, status: "compressing", error: null } : it))
    );
    try {
      const compressed = await compressImage(target.original);
      await uploadOne(target.key, compressed);
    } catch {
      setItems((prev) =>
        prev.map((it) =>
          it.key === target.key ? { ...it, status: "error", error: "Impossible de traiter cette photo." } : it
        )
      );
    }
  };

  const handleInputChange = (event) => {
    // Snapshot + clear synchronously before any async work: some browsers
    // fire both an "input" and a "change" event when .files is assigned
    // programmatically, and without this the second event would still see
    // (and re-upload) the same file list.
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    addFiles(files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    addFiles(event.dataTransfer.files);
  };

  const canAddMore = items.length < MAX_FILES;

  return (
    <div>
      <div className="flex flex-wrap gap-3">
        {items.map((item) => (
          <div
            key={item.key}
            className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-neutral-200 bg-neutral-50"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={item.previewUrl} alt="Photo jointe" className="h-full w-full object-cover" />

            {(item.status === "compressing" || item.status === "uploading") && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-center text-[9px] font-semibold leading-tight text-neutral-600">
                {item.status === "compressing" ? "Compression..." : "Envoi..."}
              </div>
            )}

            {item.status === "done" && (
              <div className="absolute right-1 top-1 rounded-full bg-white/90">
                <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
              </div>
            )}

            {item.status === "error" && (
              <button
                type="button"
                onClick={() => retryItem(item)}
                className="absolute inset-0 flex items-center justify-center bg-red-50/95 p-1 text-center text-[9px] font-semibold leading-tight text-red-600"
              >
                Échec — réessayer
              </button>
            )}

            <button
              type="button"
              onClick={() => removeItem(item)}
              aria-label="Retirer cette photo"
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>
        ))}

        {canAddMore && (
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`flex h-20 w-20 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed transition ${
              isDragging
                ? "border-accent bg-sand"
                : "border-neutral-300 bg-white hover:border-accent hover:bg-sand"
            }`}
          >
            <PhotoIcon className="h-5 w-5 text-neutral-400" />
            <span className="text-[10px] font-semibold text-neutral-500">Ajouter</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleInputChange} />
          </label>
        )}
      </div>

      <input type="hidden" name={name} value={photosLink} readOnly />

      <p className="mt-2 text-xs text-neutral-500">
        Jusqu&apos;à {MAX_FILES} photos (optionnel) — compressées et envoyées
        automatiquement, jamais stockées ailleurs qu&apos;ici.
      </p>
    </div>
  );
}
